import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Calendar,
  ChevronDown,
  Clock,
  FileText,
  Mail as MailIcon,
  Phone,
  Send,
  User,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { Button } from './Button';
import { Input } from './Input';
import { TemplateSelect } from './TemplateSelect';
import { RichTextEditor, type RichTextEditorHandle } from './RichTextEditor';
import { contactsAPI, gmailAPI } from '../../api/endpoints';
import { useDefaultTodoDays } from '../../hooks/useDefaultTodoDays';
import { useInvalidateTodos } from '../../hooks/useTodos';
import { getSchedulePresets } from '../../utils/schedulePresets';
import { hasImage, toEditableHtml, toStoredHtml } from '../../utils/emailImages';
import type { Communication, Contact, EmailTemplate, Venue } from '../../types';

export type CommunicationType = 'email' | 'phone' | 'form' | 'in-person';

/** The message a reply is being written to, picked in Previous Communications. */
export interface ReplyTarget {
  threadId: string;
  messageId: string;
  subject: string;
}

export interface CommunicationComposerProps {
  /** The contact the communication is with. Null renders nothing — see `hidden`. */
  contact: Contact | null;
  /** The venue it belongs to: it supplies `venue_id` and the favourite bands on a send. */
  venue: Venue | null;
  emailTemplates: EmailTemplate[];
  /** The contact's effective flag forbids mail (do-not-contact / expired). */
  isEmailBlocked: boolean;
  emailBlockedReason?: string;
  replyingTo: ReplyTarget | null;
  onClearReplyingTo: () => void;
  /**
   * Hands the saved communication back with the contact as the server returned it. Kept as one
   * callback so the overlay owns `contacts` and the page refreshes, and this panel owns nothing
   * but the form.
   */
  onSaved: (contactId: string, contact: Contact, communication: Communication) => void | Promise<void>;
  /**
   * The bands the mail just sent named, which the server has favorited on the venue. Only an
   * immediate send reports any: a queued one favorites them when it actually goes out.
   */
  onFavoritedBands?: (bandIds: string[]) => void;
  /** True while the contact edit form has taken over the panel. Keeps a half-typed note alive. */
  hidden?: boolean;
}

const getTodayDate = () => format(new Date(), 'yyyy-MM-dd');

/**
 * Whether the editor holds anything worth sending. TipTap serialises an empty document as
 * `<p></p>`, and a body of blank paragraphs is just as empty, so this is mostly a text test — but
 * an image is content too, and a mail that is a photo and three words of subject is a real mail.
 * Without that clause a body of nothing but a band photo reads as empty and cannot be sent.
 */
function isBodyEmpty(html: string): boolean {
  if (hasImage(html)) return false;
  return !html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim();
}

/**
 * The venue overlay's "New Communication" panel.
 *
 * It is a component rather than a block of `ContactOverlay` for one reason: it owns the state
 * that changes on every keystroke. Left in the overlay, those ~20 `useState`s meant every
 * character typed re-ran a render function carrying 81 hooks and re-rendered the venue card, the
 * map, the todos, `Modal` and the whole communication history. Here, a keystroke re-renders this
 * panel and nothing else.
 *
 * The mail body goes one step further and never reaches React state at all — see `handleBodyChange`.
 */
export function CommunicationComposer({
  contact,
  venue,
  emailTemplates,
  isEmailBlocked,
  emailBlockedReason,
  replyingTo,
  onClearReplyingTo,
  onSaved,
  onFavoritedBands,
  hidden,
}: CommunicationComposerProps) {
  const [selectedType, setSelectedType] = useState<CommunicationType>('email');
  const [emailSubject, setEmailSubject] = useState('');
  // `null` means "whatever Profile -> Settings says" — so the box follows the setting until the
  // user types a delay for this particular mail, and settles once /auth/me has answered.
  const defaultTodoDays = useDefaultTodoDays();
  const invalidateTodos = useInvalidateTodos();
  const [todoDaysOverride, setTodoDaysOverride] = useState<number | null>(null);
  const todoDays = todoDaysOverride ?? defaultTodoDays;
  const [disableTodo, setDisableTodo] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const [phoneNotes, setPhoneNotes] = useState('');
  const [phoneDate, setPhoneDate] = useState(getTodayDate);
  const [phoneDirection, setPhoneDirection] = useState<'sent' | 'received'>('sent');
  const [formNotes, setFormNotes] = useState('');
  const [formDate, setFormDate] = useState(getTodayDate);
  const [inPersonNotes, setInPersonNotes] = useState('');
  const [inPersonDate, setInPersonDate] = useState(getTodayDate);
  const [inPersonDirection, setInPersonDirection] = useState<'sent' | 'received'>('sent');

  const [isSending, setIsSending] = useState(false);
  const [isSavingPhone, setIsSavingPhone] = useState(false);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [isSavingInPerson, setIsSavingInPerson] = useState(false);

  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [customScheduleDate, setCustomScheduleDate] = useState('');
  const [customScheduleTime, setCustomScheduleTime] = useState('10:30');
  const [schedulePickerPos, setSchedulePickerPos] = useState<{ top: number; left: number } | null>(null);
  const scheduleRef = useRef<HTMLDivElement>(null);
  const scheduleButtonWrapperRef = useRef<HTMLDivElement>(null);

  // The mail body lives in a ref, never in state. Routing every keystroke back through React is
  // what re-rendered this panel — and the editor with it — on each character, and the body is
  // only ever read when the mail goes out. The one thing the UI needs is whether there is
  // anything to send, and that flips twice per email rather than per keystroke.
  const editorRef = useRef<RichTextEditorHandle>(null);
  const emailBodyRef = useRef('');
  const bodyEmptyRef = useRef(true);
  const [bodyEmpty, setBodyEmpty] = useState(true);

  const noteBody = useCallback((html: string) => {
    emailBodyRef.current = html;
    const empty = isBodyEmpty(html);
    if (bodyEmptyRef.current === empty) return;
    bodyEmptyRef.current = empty;
    setBodyEmpty(empty);
  }, []);

  /** Push content *into* the editor — a template, a reset after sending. */
  const setBody = useCallback((html: string) => {
    noteBody(html);
    editorRef.current?.setContent(html);
  }, [noteBody]);

  // Recomputed (fresh jitter included) only when the picker opens, so the times shown don't
  // shift under the user while it is sitting open.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schedulePresets = useMemo(() => getSchedulePresets(), [showSchedulePicker]);

  const venueId = venue?._id;
  // Reopening the overlay, or paging to another venue, should not leave yesterday's date in a form
  useEffect(() => {
    setPhoneDate(getTodayDate());
    setFormDate(getTodayDate());
    setInPersonDate(getTodayDate());
  }, [venueId]);

  // Picking a message in the history starts a reply. It drives the subject, the tab and the
  // template reset, so the overlay sets `replyingTo` and nothing else — it no longer reaches
  // into this component's state.
  useEffect(() => {
    if (!replyingTo) {
      setEmailSubject('');
      return;
    }
    const subject = replyingTo.subject;
    setEmailSubject(subject.startsWith('Re:') || subject.startsWith('RE:') ? subject : `Re: ${subject}`);
    setSelectedType('email');
    setSelectedTemplateId('');
  }, [replyingTo]);

  const contactHasEmail = Boolean(contact?.email);

  // Switch to phone if the selected contact has no email, or is blocked, and email is selected
  useEffect(() => {
    if (contact && selectedType === 'email' && (!contact.email || isEmailBlocked)) {
      setSelectedType('phone');
    }
  }, [contact, selectedType, isEmailBlocked]);

  // Close the schedule picker on an outside click
  useEffect(() => {
    if (!showSchedulePicker) return;

    // Stop mousedown inside the portal from reaching the document handler
    const portalEl = scheduleRef.current;
    const stopProp = (e: MouseEvent) => e.stopPropagation();
    portalEl?.addEventListener('mousedown', stopProp);

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (scheduleButtonWrapperRef.current?.contains(target)) return;
      setShowSchedulePicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      portalEl?.removeEventListener('mousedown', stopProp);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSchedulePicker]);

  const handleApplyTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;

    const template = emailTemplates.find(t => t._id === templateId);
    if (template) {
      if (!replyingTo) setEmailSubject(template.subject);
      setBody(toEditableHtml(template.email_text));
    }
  };

  const canSendEmail = Boolean(emailSubject && !bodyEmpty && contact?.email);

  const handleSendEmail = (scheduleDate?: string | null) => {
    if (!canSendEmail) return;
    setScheduledAt(scheduleDate ?? null);
    setShowSendConfirm(true);
    setShowSchedulePicker(false);
  };

  const handleScheduleCustom = () => {
    if (!customScheduleDate || !customScheduleTime) return;
    const dt = new Date(`${customScheduleDate}T${customScheduleTime}:00`);
    handleSendEmail(dt.toISOString());
  };

  const handleConfirmSendEmail = async () => {
    // The editor's document holds blob URLs so the images display; what is stored and sent holds
    // `cid:` references (docs/EMAIL_IMAGES.md). This is that boundary — it runs once per send,
    // never per keystroke.
    const body = toStoredHtml(emailBodyRef.current);
    if (!emailSubject || isBodyEmpty(body) || !contact?.email) return;

    const isScheduling = !!scheduledAt;
    setShowSendConfirm(false);
    setIsSending(true);
    try {
      const sendPayload = {
        contact_id: contact._id,
        to: contact.email,
        subject: emailSubject,
        body,
        create_todo: !disableTodo,
        todo_days: todoDays,
        ...(replyingTo && {
          ...(replyingTo.threadId && { thread_id: replyingTo.threadId }),
          ...(replyingTo.messageId && { in_reply_to: replyingTo.messageId }),
        }),
        ...(venue && { venue_id: venue._id }),
        ...(venue?.favorite_band_ids && venue.favorite_band_ids.length > 0 && { band_ids: venue.favorite_band_ids }),
        ...(scheduledAt && { scheduled_at: scheduledAt }),
      };
      const sendResponse = await gmailAPI.send(sendPayload);

      // A band named in the mail is now a favorite of the venue, server-side. Nothing here
      // refetches the venue, so hand the ids up rather than leave the stars a reload behind.
      const favoritedBandIds = sendResponse.data?.favorited_band_ids;
      if (favoritedBandIds?.length) onFavoritedBands?.(favoritedBandIds);

      // The send moved todos server-side without anything here asking for it: the contact's
      // still-pending todos are marked done, and an immediate send also writes the follow-up
      // (docs/EMAIL_SEND.md). Nothing refetches on its own, so the overlay's todo list — and the
      // navbar badge — would keep showing the state from before the mail left.
      invalidateTodos();

      // Mark all existing communications for this contact as read
      contactsAPI.markAllRead(contact._id).catch(() => {});

      // Refresh contact to show new communication in history
      const response = await contactsAPI.getById(contact._id);
      await onSaved(contact._id, response.data, { type: 'email' } as Communication);

      toast.success(isScheduling ? 'Email scheduled!' : 'Email sent!');
      setEmailSubject('');
      setBody('');
      setTodoDaysOverride(null);
      setDisableTodo(false);
      setSelectedTemplateId('');
      onClearReplyingTo();
      setScheduledAt(null);
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error(isScheduling ? 'Failed to schedule email' : 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const handleAddPhone = async () => {
    if (!phoneNotes || !phoneDate || !contact) return;

    setIsSavingPhone(true);
    try {
      const communication: Communication = {
        type: 'phone',
        phone: {
          date: phoneDate,
          commentary: phoneNotes,
          direction: phoneDirection,
        },
      };

      const response = await contactsAPI.addCommunication(contact._id, communication);
      await onSaved(contact._id, response.data, communication);

      setPhoneNotes('');
      setPhoneDate(getTodayDate());
      setPhoneDirection('sent');
    } finally {
      setIsSavingPhone(false);
    }
  };

  const handleAddForm = async () => {
    if (!formNotes || !formDate || !contact) return;

    setIsSavingForm(true);
    try {
      const communication: Communication = {
        type: 'form',
        form: {
          date: formDate,
          commentary: formNotes,
        },
      };

      const response = await contactsAPI.addCommunication(contact._id, communication);
      await onSaved(contact._id, response.data, communication);

      setFormNotes('');
      setFormDate(getTodayDate());
    } finally {
      setIsSavingForm(false);
    }
  };

  const handleAddInPerson = async () => {
    if (!inPersonNotes || !inPersonDate || !contact) return;

    setIsSavingInPerson(true);
    try {
      const communication: Communication = {
        type: 'other',
        other: {
          date: inPersonDate,
          commentary: inPersonNotes,
          direction: inPersonDirection,
        },
      };

      const response = await contactsAPI.addCommunication(contact._id, communication);
      await onSaved(contact._id, response.data, communication);

      setInPersonNotes('');
      setInPersonDate(getTodayDate());
      setInPersonDirection('sent');
    } finally {
      setIsSavingInPerson(false);
    }
  };

  // Rendered as nothing rather than dropped by the parent, so a half-typed note survives a trip
  // through the contact edit form.
  if (!contact || hidden) return null;

  return (
    <>
      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-semibold text-dark-text-primary flex-shrink-0">New Communication</h4>

        {/* Type Selector */}
        <div className="flex gap-1 flex-shrink-0 items-center">
          <Button
            variant={selectedType === 'email' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSelectedType('email')}
            disabled={!contactHasEmail || isEmailBlocked}
            className={(!contactHasEmail || isEmailBlocked) ? 'opacity-50 cursor-not-allowed' : ''}
            title={isEmailBlocked ? emailBlockedReason : (!contactHasEmail ? 'Email address needed' : undefined)}
          >
            <MailIcon className="w-3 h-3" />
            {replyingTo ? 'Email (Re:)' : 'Email'}
          </Button>
          <Button
            variant={selectedType === 'phone' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSelectedType('phone')}
          >
            <Phone className="w-3 h-3" />
            Phone
          </Button>
          <Button
            variant={selectedType === 'form' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSelectedType('form')}
          >
            <FileText className="w-3 h-3" />
            Form
          </Button>
          <Button
            variant={selectedType === 'in-person' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSelectedType('in-person')}
          >
            <User className="w-3 h-3" />
            In Person
          </Button>
        </div>

        {/* Form Content */}
        <div className="flex-1">
          {/* Email Form */}
          {selectedType === 'email' && (
            <div className="space-y-3">
              {/* Template Selector */}
              {emailTemplates.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      Template
                    </div>
                  </label>
                  <TemplateSelect
                    templates={emailTemplates}
                    value={selectedTemplateId}
                    onChange={handleApplyTemplate}
                  />
                </div>
              )}

              <Input
                label="Subject"
                value={emailSubject}
                onChange={(e) => !replyingTo && setEmailSubject(e.target.value)}
                placeholder="Email subject..."
                className={clsx('w-full', replyingTo && 'opacity-60')}
                readOnly={!!replyingTo}
              />

              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                  Message
                </label>
                {/* Uncontrolled on purpose: `content` is the seed, `setBody` pushes a template in,
                    and `onChange` only records the html in a ref (see the note above). */}
                <RichTextEditor
                  ref={editorRef}
                  content=""
                  onChange={noteBody}
                  placeholder="Compose your email..."
                />
              </div>

              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  label="Todo (days)"
                  value={todoDays}
                  onChange={(e) => setTodoDaysOverride(Number(e.target.value))}
                  className="w-24"
                  min={1}
                />

                <label className="flex items-center gap-2 cursor-pointer mt-5">
                  <input
                    type="checkbox"
                    checked={disableTodo}
                    onChange={(e) => setDisableTodo(e.target.checked)}
                    className="w-3 h-3 rounded-sm border-dark-border bg-dark-elevated"
                  />
                  <span className="text-xs text-dark-text-secondary">
                    Disable todo
                  </span>
                </label>
              </div>

              <div className="flex gap-2 relative">
                <Button
                  onClick={() => handleSendEmail()}
                  isLoading={isSending}
                  size="sm"
                  disabled={isEmailBlocked || !canSendEmail}
                  title={isEmailBlocked ? emailBlockedReason : !canSendEmail ? 'Subject and message are required' : undefined}
                >
                  <Send className="w-3 h-3" />
                  {replyingTo ? 'Reply' : 'Send'}
                </Button>
                <div className="relative" ref={scheduleButtonWrapperRef}>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isEmailBlocked || !canSendEmail}
                    title={isEmailBlocked ? emailBlockedReason : !canSendEmail ? 'Subject and message are required' : undefined}
                    onClick={() => {
                      if (!showSchedulePicker && scheduleButtonWrapperRef.current) {
                        const rect = scheduleButtonWrapperRef.current.getBoundingClientRect();
                        setSchedulePickerPos({
                          top: rect.top,
                          left: rect.right - 288, // 288px = w-72
                        });
                        const now = new Date();
                        setCustomScheduleDate(format(now, 'yyyy-MM-dd'));
                        setCustomScheduleTime(format(now, 'HH:mm'));
                      }
                      setShowSchedulePicker(!showSchedulePicker);
                    }}
                  >
                    <Clock className="w-3 h-3" />
                    Schedule
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                  {showSchedulePicker && schedulePickerPos && createPortal(
                    <div
                      ref={scheduleRef}
                      className="fixed w-72 bg-dark-surface border border-dark-border rounded-lg shadow-xl overflow-hidden"
                      style={{ top: schedulePickerPos.top, left: schedulePickerPos.left, transform: 'translateY(-100%) translateY(-4px)', zIndex: 10000 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-2 border-b border-dark-border">
                        <p className="text-xs font-medium text-dark-text-secondary px-2 py-1">Schedule send</p>
                      </div>
                      {/* The popover's bottom is pinned to the Schedule button and it grows
                          upwards, so the preset list scrolls rather than running off the top of
                          a short window. */}
                      <div className="py-1 max-h-60 overflow-y-auto">
                        {schedulePresets.map((preset) => (
                          <button
                            key={preset.value}
                            onClick={() => handleSendEmail(preset.value)}
                            className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-dark-elevated transition-colors"
                          >
                            <span className="text-dark-text-primary">{preset.label}</span>
                            <span className="text-dark-text-tertiary">{preset.sublabel}</span>
                          </button>
                        ))}
                      </div>
                      <div className="p-2 border-t border-dark-border space-y-2">
                        <p className="text-xs font-medium text-dark-text-secondary px-1">Custom</p>
                        <div className="flex gap-2">
                          <input
                            type="date"
                            value={customScheduleDate}
                            onChange={(e) => setCustomScheduleDate(e.target.value)}
                            min={format(new Date(), 'yyyy-MM-dd')}
                            className="flex-1 px-2 py-1.5 text-xs rounded-md bg-dark-elevated border border-dark-border text-dark-text-primary"
                          />
                          <input
                            type="time"
                            value={customScheduleTime}
                            onChange={(e) => setCustomScheduleTime(e.target.value)}
                            className="w-24 px-2 py-1.5 text-xs rounded-md bg-dark-elevated border border-dark-border text-dark-text-primary"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="primary"
                          className="w-full"
                          disabled={!customScheduleDate || !customScheduleTime}
                          onClick={handleScheduleCustom}
                        >
                          <Calendar className="w-3 h-3" />
                          Schedule for {customScheduleDate && customScheduleTime
                            ? format(new Date(`${customScheduleDate}T${customScheduleTime}`), 'MMM d, HH:mm')
                            : '...'}
                        </Button>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Phone Form */}
          {selectedType === 'phone' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                  Direction
                </label>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPhoneDirection('sent')}
                    className={clsx(
                      'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      phoneDirection === 'sent'
                        ? 'bg-primary-600 text-white'
                        : 'bg-dark-elevated border border-dark-border text-dark-text-secondary hover:text-dark-text-primary'
                    )}
                  >
                    <ArrowUpRight className="w-3 h-3" />
                    Sent
                  </button>
                  <button
                    onClick={() => setPhoneDirection('received')}
                    className={clsx(
                      'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      phoneDirection === 'received'
                        ? 'bg-primary-600 text-white'
                        : 'bg-dark-elevated border border-dark-border text-dark-text-secondary hover:text-dark-text-primary'
                    )}
                  >
                    <ArrowDownLeft className="w-3 h-3" />
                    Received
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={phoneDate}
                  onChange={(e) => setPhoneDate(e.target.value)}
                  max={getTodayDate()}
                  className="w-full px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-dark-text-primary text-sm focus:outline-hidden focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                  Notes
                </label>
                <textarea
                  value={phoneNotes}
                  onChange={(e) => setPhoneNotes(e.target.value)}
                  placeholder="Call notes..."
                  rows={3}
                  className="input w-full resize-y min-h-16"
                />
              </div>
              <Button onClick={handleAddPhone} isLoading={isSavingPhone} size="sm">
                Save Phone Call
              </Button>
            </div>
          )}

          {/* Online Form */}
          {selectedType === 'form' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  max={getTodayDate()}
                  className="w-full px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-dark-text-primary text-sm focus:outline-hidden focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                  Notes
                </label>
                <textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Form submission notes..."
                  rows={3}
                  className="input w-full resize-y min-h-16"
                />
              </div>
              <Button onClick={handleAddForm} isLoading={isSavingForm} size="sm">
                Save Form Submission
              </Button>
            </div>
          )}

          {/* In Person Form */}
          {selectedType === 'in-person' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                  Direction
                </label>
                <div className="flex gap-1">
                  <button
                    onClick={() => setInPersonDirection('sent')}
                    className={clsx(
                      'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      inPersonDirection === 'sent'
                        ? 'bg-primary-600 text-white'
                        : 'bg-dark-elevated border border-dark-border text-dark-text-secondary hover:text-dark-text-primary'
                    )}
                  >
                    <ArrowUpRight className="w-3 h-3" />
                    Sent
                  </button>
                  <button
                    onClick={() => setInPersonDirection('received')}
                    className={clsx(
                      'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      inPersonDirection === 'received'
                        ? 'bg-primary-600 text-white'
                        : 'bg-dark-elevated border border-dark-border text-dark-text-secondary hover:text-dark-text-primary'
                    )}
                  >
                    <ArrowDownLeft className="w-3 h-3" />
                    Received
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={inPersonDate}
                  onChange={(e) => setInPersonDate(e.target.value)}
                  max={getTodayDate()}
                  className="w-full px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-dark-text-primary text-sm focus:outline-hidden focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                  Notes
                </label>
                <textarea
                  value={inPersonNotes}
                  onChange={(e) => setInPersonNotes(e.target.value)}
                  placeholder="Meeting notes..."
                  rows={3}
                  className="input w-full resize-y min-h-16"
                />
              </div>
              <Button onClick={handleAddInPerson} isLoading={isSavingInPerson} size="sm">
                Save Meeting
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Send Confirmation Modal */}
      {showSendConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-surface border border-dark-border rounded-lg p-4 max-w-sm">
            <h3 className="text-sm font-semibold text-dark-text-primary mb-2">
              {scheduledAt ? 'Schedule Email?' : 'Send Email?'}
            </h3>
            <p className="text-xs text-dark-text-secondary mb-1">
              <span className="font-medium">To:</span> {contact.email}
            </p>
            <p className="text-xs text-dark-text-secondary mb-1">
              <span className="font-medium">Subject:</span> {emailSubject}
            </p>
            {scheduledAt && (
              <p className="text-xs text-primary-400 mb-1">
                <span className="font-medium">Scheduled for:</span>{' '}
                {format(new Date(scheduledAt), 'EEE MMM d, yyyy \'at\' HH:mm')}
              </p>
            )}
            <p className="text-xs text-dark-text-muted mb-1">
              <span className="font-medium">Follow-up:</span>{' '}
              {disableTodo
                ? 'no todo'
                : scheduledAt
                ? `todo ${todoDays} day${todoDays !== 1 ? 's' : ''} after it goes out`
                : `todo in ${todoDays} day${todoDays !== 1 ? 's' : ''}`}
            </p>
            <div className="h-2" />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowSendConfirm(false)}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleConfirmSendEmail}>
                {scheduledAt ? <Clock className="w-3 h-3" /> : <Send className="w-3 h-3" />}
                {scheduledAt ? 'Schedule' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
